function formatBriefingStamp(updatedAt) {
  const text = String(updatedAt || '').trim();
  const match = text.match(
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/,
  );
  if (!match) return '';
  const [, year, month, day, hour, minute, second] = match;
  return `${month}/${day}/${year.slice(2)} ${hour}:${minute}:${second}`;
}

const BRIEFING_HINT_KEY = 'mworkorders-briefing-hint-seen';

function briefingHintSeen(storage) {
  try {
    return storage.getItem(BRIEFING_HINT_KEY) === '1';
  } catch {
    return true;
  }
}

function markBriefingHintSeen(storage) {
  try {
    storage.setItem(BRIEFING_HINT_KEY, '1');
  } catch {
    /* storage can be unavailable */
  }
}

function briefingMetaLine(name, updatedAt) {
  const who = String(name || '').trim();
  const when = formatBriefingStamp(updatedAt);
  if (!who && !when) return '';
  if (!who) return when;
  if (!when) return `edit by ${who}`;
  return `edit by ${who}  ${when}`;
}

function briefingWorkOrderIds(line) {
  return [...String(line || '').matchAll(/search=:(\d+)/g)].map((match) =>
    Number(match[1]),
  );
}

function briefingDuplicateIds(markdown, workOrderId) {
  const id = Number(workOrderId);
  const found = new Set();
  for (const line of String(markdown || '').split('\n')) {
    if (!/^-\s/.test(line)) continue;
    const ids = briefingWorkOrderIds(line);
    if (!ids.includes(id)) continue;
    for (const other of ids) {
      if (other !== id) found.add(other);
    }
  }
  return [...found];
}

function briefingIdsToCloseTogether(markdown, workOrderId) {
  const id = Number(workOrderId);
  if (!id) return [];
  return [id, ...briefingDuplicateIds(markdown, id)];
}

function rewriteBriefingWorkOrderBullet(line, keepIds) {
  const keep = new Set(keepIds.map(Number));
  const marked = String(line).match(/^(- \*\*)(.*)(\*\*)(.*)$/);
  if (!marked) return null;
  const parts = marked[2].split(/,\s*also\s+/);
  const kept = parts.filter((part) => {
    const id = part.match(/search=:(\d+)/);
    if (!id) return true;
    return keep.has(Number(id[1]));
  });
  if (!kept.length) return '';
  return `${marked[1]}${kept.join(', also ')}${marked[3]}${marked[4]}`;
}

function removeClosedWorkOrdersFromBriefing(markdown, closedIds) {
  const closed = new Set(
    (closedIds || []).map(Number).filter((id) => id > 0),
  );
  const text = String(markdown ?? '');
  if (!closed.size || !text) return text;
  const lines = text.split('\n');
  const out = [];
  const removed = new Set();
  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];
    if (!/^-\s/.test(line)) {
      out.push(line);
      continue;
    }
    const ids = briefingWorkOrderIds(line);
    const closing = ids.filter((id) => closed.has(id));
    if (!closing.length) {
      out.push(line);
      continue;
    }
    const keep = ids.filter((id) => !closed.has(id));
    let quoteEnd = i;
    while (
      quoteEnd + 1 < lines.length &&
      /^\s*>/.test(lines[quoteEnd + 1])
    ) {
      quoteEnd += 1;
    }
    if (!keep.length) {
      closing.forEach((id) => removed.add(id));
      i = quoteEnd;
      continue;
    }
    const rewritten = rewriteBriefingWorkOrderBullet(line, keep);
    if (rewritten == null) {
      out.push(line);
      continue;
    }
    closing.forEach((id) => removed.add(id));
    out.push(rewritten);
  }
  if (!removed.size) return text;
  const next = out.join('\n').replace(/^(\d+)( open\b)/m, (_match, count, rest) => {
    const left = Math.max(0, Number(count) - removed.size);
    return `${left}${rest}`;
  });
  return next;
}

module.exports = {
  formatBriefingStamp,
  briefingMetaLine,
  briefingHintSeen,
  markBriefingHintSeen,
  BRIEFING_HINT_KEY,
  briefingWorkOrderIds,
  briefingDuplicateIds,
  briefingIdsToCloseTogether,
  removeClosedWorkOrdersFromBriefing,
};
